import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, MessagesSquare, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { PillTabs } from '../components/alt/PillTabs';
import { SectionIntro } from '../components/alt/SectionIntro';

// Order carries the argument: connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'workspace',
  label: 'Workspace',
  icon: MessagesSquare
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  // An IntersectionObserver band was mis-firing here: these sections are much
  // taller than the band, so with direction-dependent tie-breaking the active
  // tab could jump a section early or lag a section behind. A single reference
  // line just below the sticky nav is unambiguous — the active section is
  // simply the last one whose top has passed it.
  useEffect(() => {
    const update = () => {
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 140) + 24;
      let current: SectionId = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= line) current = section.id;
      }
      setActiveSection(current);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    // The pillar bar is the ONLY thing pinned to the top of the viewport on
    // this page — the site header scrolls away — so the section only needs to
    // clear the bar's own height, plus a little breathing room.
    // Align the section's FIRST piece of content (its eyebrow) rather than the
    // section box — the box carries 80–112px of its own top padding, which
    // otherwise reads as dead space under the bar. Falls back to the box.
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const anchor = el.querySelector('p, h2, h3') ?? el;
    const top = anchor.getBoundingClientRect().top + window.scrollY - navHeight - 18;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="giga-home relative min-h-screen bg-[#FAF8F4] pt-24 text-[#2B211A]" data-id="element-5982" data-mp="39jdavxzzrcue">
      {/* The halftone is a texture, not a pattern to stare at: it sits behind
       the hero only and fades out radially, so the headline reads off clean
       paper while the page still has a surface. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[720px]" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
      backgroundSize: '8px 8px',
      WebkitMaskImage: 'radial-gradient(115% 90% at 50% 8%, #000 8%, rgba(0,0,0,0.55) 45%, transparent 82%)',
      maskImage: 'radial-gradient(115% 90% at 50% 8%, #000 8%, rgba(0,0,0,0.55) 45%, transparent 82%)'
    }} data-id="element-5983" data-mp="2lt981sumnh4g" />
      <section className="relative pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5984" data-mp="1pnphko0h0iwi">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5985" data-mp="1plcy26f2e0hc">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5986" data-mp="1lkjcvkt0azs5">
            <motion.h1 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            ease: [0.23, 1, 0.32, 1]
          }} className="font-title text-5xl font-bold leading-[0.95] tracking-[-0.028em] text-[#2B211A] [text-wrap:balance] md:text-7xl" data-id="element-5987" data-mp="1gn1cwjwyvuwa">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5988" data-mp="xwtegf47psmf">
                <AnimatePresence mode="wait" initial={false} data-id="element-5989" data-mp="sx7185odwcdm">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5990" data-mp="swnjoq2nbq04">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5991" data-mp="11yr8pzmosieb" />
              that want to <HeadlineIcon icon={Zap} data-id="element-5992" data-mp="12259x4jm6lk6" />
              <UnderscoredPhrase data-id="element-5993" data-mp="nbpqnvardnon">Go Faster</UnderscoredPhrase>
              <br data-id="element-5994" data-mp="n9bwdonwkfi5" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-5995" data-mp="s9zneac4swcd" />
              <UnderscoredPhrase data-id="element-5996" data-mp="s8oj89erxmkq">Stay in Control</UnderscoredPhrase>
            </motion.h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-0 z-40 border-b border-[#E7E0D4]/70 bg-[#FAF8F4]/70 shadow-[0_1px_0_rgba(255,255,255,0.6)_inset] backdrop-blur-xl" data-id="element-5997" data-mp="1kqk1bsxug1li">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5998" data-mp="35feeogs0ngu2">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-[0_10px_24px_-10px_rgba(234,88,12,0.75)] hover:bg-[#C2410C]' : 'border-[#E7E0D4] bg-white text-[#6B5D52] shadow-[0_1px_2px_rgba(43,33,26,0.04)] hover:-translate-y-px hover:border-[#D9CFBF] hover:text-[#2B211A] hover:shadow-[0_6px_16px_-8px_rgba(43,33,26,0.35)]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5999" data-mp="3a9saulei5rzs">
                <Icon size={19} strokeWidth={1.9} className={`shrink-0 transition-colors duration-200 ${isActive ? 'text-white' : 'text-[#A99A8C]'}`} data-id="element-6000" data-mp="259u2r7ccf75k" />
                <span className="min-w-0 leading-tight" data-id="element-6001" data-mp="2a613mlpvtsgn">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-6002" data-mp="2j0ywxtwxz78i">
        <section id="connected-apps" className="scroll-mt-24 border-b border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6003" data-mp="2iz5r2gu5kr1k">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6004" data-mp="2nxpbudx0fwsc">
            <ConnectedAppsPillar data-id="element-6005" data-mp="2sy3n04twrmul" />
            <div className="mt-20 md:mt-28" data-id="element-6006" data-mp="2sz4xa733o5b9">
              <ConnectedAppsTable data-id="element-6007" data-mp="2xx6f6ooshyl5" />
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-24 border-b border-[#E7E0D4] bg-white py-24 md:py-32" data-id="element-6008" data-mp="ykc4eftlpsn0">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6009" data-mp="13j5l0h46pzre">
            <BrainFlowExperiment data-id="element-6010" data-mp="13ljc6evqurto" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-24 border-b border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6011" data-mp="3gui12dzk9ffs">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6012" data-mp="3c1f37tnr3jts">
            <SectionIntro className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-6013" data-mp="380lkdep60v7r">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-6014" data-mp="332ts2jklz731">
                AI Co Worker
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-6015" data-mp="3mn401113r2w">
                An AI teammate
                <br data-id="element-6016" data-mp="3qrcysuo0fbt0" />
                inside your existing tools
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-6017" data-mp="3lsry6u4uiq41">
                Add Giga to Slack, ChatGPT, Claude Code or Cowork, and talk to it like you would a normal employee. 
              </p>
            </SectionIntro>

            {/* One centred column: the surface switcher sits directly above the
             frame it controls, so the relationship needs no explaining. */}
            <div className="mx-auto max-w-[820px]" data-id="element-6018" data-mp="1vw4058hngt8n">
              {/* Deliberately quieter and smaller than the four pillar tabs at
               the top of the page — this is a local control, not navigation —
               but the platform logos stay so each one is identifiable. */}
              <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden justify-center md:mb-10 md:flex" data-id="element-6019" data-mp="2jkwn4fganqr4" />
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6020" data-mp="2elttdr5rl6yg" />
            </div>
          </div>
        </section>

        <section id="workspace" className="scroll-mt-24 bg-white py-24 md:py-32" data-id="element-6021" data-mp="2ojq2yej1ztug">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6022" data-mp="z5bk2aupv16v">
            <PlatformPillar data-id="element-6023" data-mp="z5lafzjobad3" />
          </div>
        </section>

        {/* White canvas: the diagram's group panels are cream, so they need a
         lighter ground now that the enclosing card is gone. */}
        <section className="border-t border-[#E7E0D4] bg-white py-24 md:py-32" data-id="element-6024" data-mp="35udfcpipzahe">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6025" data-mp="3as597p2xd6o8">
            <ArchitectureWidget data-id="element-6026" data-mp="2w4aczyppkk6e" />
          </div>
        </section>

        <section className="border-t border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6027" data-mp="2w1xyjvj5sry3">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6028" data-mp="310si4zoyakj9">
            <AccelerationControlSection data-id="element-6029" data-mp="2n219vtwrrt31" />
            <ClosingCTA data-id="element-6030" data-mp="2n5gokvmj703x" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  // Bare mark, no tile: at headline scale the filled square read as a button
  // and boxed the icon away from the words it belongs to.
  return <span aria-hidden="true" className="mr-[0.14em] inline-flex size-[0.78em] -translate-y-[0.04em] items-center justify-center align-middle" data-id="element-6031" data-mp="becoe2o6i96q">
      <Icon className="size-full text-[#EA580C]" strokeWidth={2.4} data-id="element-6032" data-mp="ev7i1kobbo8q" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  // The rule draws itself left-to-right on load — one small piece of craft that
  // makes the headline feel authored rather than typeset.
  return <span className="relative inline-block align-baseline" data-id="element-6033" data-mp="3w445rq2p4gbz">
      <motion.span initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.62,
      delay: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute inset-x-0 bottom-[-4px] h-[9px] origin-left rounded-[2px] bg-[#EA580C]" aria-hidden="true" data-id="element-6034" data-mp="b20la9hwl3" />
      <span className="relative z-10" data-id="element-6035" data-mp="nucaqad70ncz">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-6036" data-mp="1blzwkdvll88e" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6037" data-mp="1nwhgj6h66yc7">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6038" data-mp="1sxexjfqg1mc4" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-6039" data-mp="22trrmepwi27e">
          <MoreThanAnswers embedded data-id="element-6040" data-mp="26t0klhd49w1w" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6041" data-mp="2bnmwrmr0vsn1">
        {mobileTabs}
        <CodeTerminal data-id="element-6042" data-mp="2lkiuqyjaowdc" />
      </div>;
  }
  // Width matches SlackPanel's own max-width so the orange halo hugs the frame
  // instead of showing a strip of background down each side.
  return <div className="relative mx-auto w-full max-w-[720px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6043" data-mp="3n9xydt2pw4qv">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6044" data-mp="3ia37vqmuth6c" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-6045" data-mp="ttnw2bs4ykf">
        <SlackPanel className="w-full" heightClass="h-[520px] sm:h-[600px]" data-id="element-6046" data-mp="3s1fvg61mdl6k" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6047" data-mp="skrhr9pvup7h" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-6048" data-mp="w371o55at11j" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6049" data-mp="114e8a0vpfu6m" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6050" data-mp="m7na1uxrooil">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-6051" data-mp="1ewaxnx5znpzb">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  // Same control as the architecture diagram's preset tabs, with each
  // platform's logo added so the surfaces are identifiable at a glance.
  return <div className={`w-full items-center ${className}`} data-id="element-6052" data-mp="27gxyo3tgq53s">
      <PillTabs ariaLabel="Chat surface" value={activeSurface} onChange={id => setActiveSurface(id as SurfaceKey)} className={tabClassName} items={[{
      id: 'slack',
      label: 'Slack',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
    }, {
      id: 'code',
      label: 'Claude',
      ariaLabel: 'Claude',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude-ai.svg'
    }, {
      id: 'chatgpt',
      label: 'ChatGPT',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/chatgpt.svg'
    }]} data-id="element-6053" data-mp="27hhi5oyrh1jx" />
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-6054" data-mp="1db6cc04b33i7">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-6055" data-mp="18cwfll2xnuuh" data-mpi={groupIndex}>
          <SectionIntro align="left" data-id="element-6056" data-mp="1na5ldjszh15q">
            <h2 className="font-title text-4xl font-bold leading-tight tracking-[-0.025em] text-[#2B211A] md:text-6xl" data-id="element-6057" data-mp="1nb6x7qhtproi">
              {group.heading}
            </h2>
          </SectionIntro>
          <div className="mt-8 border-y border-[#E7E0D4]" data-id="element-6058" data-mp="1wa24z70in9tx">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-[#E7E0D4] py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-6059" data-mp="1rca9y44kr7li" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight tracking-[-0.02em] text-[#2B211A] md:text-3xl" data-id="element-6060" data-mp="2tp3dufju9ojy">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-6061" data-mp="2yl0ukmz0kwx9">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  // The closing panel is the one dark surface on the page, so it carries the
  // brand halftone rather than reading as a flat black rectangle.
  return <div className="relative mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center shadow-[0_40px_100px_-45px_rgba(43,33,26,0.7)] md:mt-28 md:px-16 md:py-20" data-id="element-6062" data-mp="2fpapurzp5mfo">
      <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-6063" data-mp="25xxxlz9zku94" />
      <div className="relative" data-id="element-6064" data-mp="1ryeq98p01mxv">
        <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-[-0.025em] text-white md:text-[52px]" data-id="element-6065" data-mp="1rz6neny8vsq9">
          One platform to run your company's AI on.
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-6066" data-mp="1w12nsbo67kvd">
          Connect your tools, build your workflows, put a co worker where your
          team already is, and let the brain get sharper every week.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-6067" data-mp="1r31902xs27pp">
          <a href="https://app.getgiga.com/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white shadow-[0_14px_30px_-12px_rgba(234,88,12,0.85)] transition-[background-color,transform,box-shadow] duration-200 ease-out hover:-translate-y-px hover:bg-[#C2410C]" data-id="element-6068" data-mp="2et3vm3ixalgx">
            Get started for free
            <ArrowRight size={17} data-id="element-6069" data-mp="2a0ar9grd9f5d" />
          </a>
          <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-[border-color,background-color] duration-200 ease-out hover:border-white/60 hover:bg-white/5" data-id="element-6070" data-mp="2xr7snzljcu2d">
            Book a demo
          </Link>
        </div>
        <p className="mt-6 text-[13.5px] text-white/45" data-id="element-6071" data-mp="m55wrpjgca84">
          $100 in credits included · no card needed
        </p>
      </div>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04),0_30px_80px_-48px_rgba(43,33,26,0.4)] md:overflow-visible" data-id="element-6072" data-mp="2jqixk9zkanrt">
      {/* Horizontal scroll is only needed on narrow screens, and it clips the
       hover panel — so above md the table simply lays out at full width. */}
      <div className="overflow-x-auto md:overflow-visible" data-id="element-6073" data-mp="9mbm0n50p79i">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-6074" data-mp="9oy2xddssz4e">
          <thead data-id="element-6075" data-mp="dnx5irc24jso">
            <tr className="border-b border-[#E7E0D4]" data-id="element-6076" data-mp="dqjcaor4iv9u">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6077" data-mp="3w37ntciirp0f">
                <span className="sr-only" data-id="element-6078" data-mp="4w4w8jyrrgpo">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#8A7B70] md:text-base" data-id="element-6079" data-mp="4u0k5eqpvbeb">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#8A7B70] md:text-base" data-id="element-6080" data-mp="3r44kbwi6tquk">
                Claude
              </th>
              {/* The Giga column is the answer the table exists to give, so it
               is stated as a column rather than left level with the other two. */}
              <th scope="col" className="w-[18%] rounded-tr-[19px] border-x border-[#F3DECB] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6081" data-mp="1bcywvzeuk91m">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6082" data-mp="1lgx4exse3lh5">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="group border-b border-[#F1ECE3] last:border-b-0 transition-colors duration-150 ease-out hover:bg-[#FDFBF8]" data-id="element-6083" data-mp="1le16vakgeyt8">
                {/* The description is the detail, not the point — it appears
                 over the row on hover so ten rows stay scannable, and it
                 overlays rather than expands so nothing below it moves. */}
                <th scope="row" className="relative px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6084" data-mp="246dhekl9h6eg">
                  <span className="block" data-id="element-6085" data-mp="2e4tagt3umpql">{row.feature}</span>
                  <span className="pointer-events-none absolute left-4 right-2 top-[calc(100%-6px)] z-20 max-w-xl translate-y-1 rounded-xl border border-[#EFEAE1] bg-white px-3 py-2 text-[12.5px] font-normal leading-[1.45] text-[#6B5D52] opacity-0 shadow-[0_18px_40px_-20px_rgba(43,33,26,0.35)] transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 md:left-6" data-id="element-6086" data-mp="2e5b19a7qsbr4">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6087" data-mp="2xtc3i9ekg9ih">
                  <ComparisonValue value={row.chatgpt} data-id="element-6088" data-mp="2xqpxie7bq22d" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6089" data-mp="31tuww7w3xnms">
                  <ComparisonValue value={row.anthropic} data-id="element-6090" data-mp="36sz44szh1bah" />
                </td>
                <td className="border-x border-[#F3DECB] bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A] group-last:rounded-br-[19px] group-last:border-b-0" data-id="element-6091" data-mp="3bk7dxq4pqyo6">
                  <ComparisonValue value={row.giga} highlight data-id="element-6092" data-mp="3ghz8krwqsyw2" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6093" data-mp="1dy6m14e6y2dm">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6094" data-mp="19xeb86po7pf8" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-[#D8CFC2]" aria-label="Not supported" data-id="element-6095" data-mp="zwu6gu2y02s9">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6096" data-mp="zz6xdwqohnhg" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-[#D8CFC2]" data-id="element-6097" data-mp="1slj8cvbtoc52">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6098" data-mp="1snm8472s1jmz">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6099" data-mp="2gblzn97wxe2m">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6100" data-mp="2gcorpkgz1mk4">
        <Icon size={22} strokeWidth={2.4} data-id="element-6101" data-mp="2bisuik8j83q0" />
      </span>
      {label}
    </p>;
}